<template>
  <Modal :title="`${provider} Text-to-speech Settings`" :show="true" @close="$emit('close')">
    <div class="form--wrapper general">
      <div class="form-group">
        <div class="form-group--inside">
          <label>Preferred voice type</label>
          <Select
            :elements="options.textToSpeechPreferredVoiceType"
            v-model="newSettings.textToSpeechPreferredVoiceType"
            @change="checkSettings"
            keyAttr="label"
            valueAttr="id"
            class="white-select"
          ></Select>
        </div>

        <div v-show="options.textToSpeechSpeed" class="form-group--inside">
          <label>Speed</label><br />
          <input type="range" :value="newSettings.textToSpeechSpeed" :min="speedMin" :max="speedMax" :step="speedStep" @change="changeSpeed" />
          {{ newSettings.textToSpeechSpeed }}
        </div>

        <div v-show="options.textToSpeechDialects || Object.keys(languiagesWithDialects).length" class="form-group--inside">
          <label>Preferred language dialect</label>
          <div class="row table-row" v-for="option in Object.entries(newSettings.textToSpeechDialects || {})" :key="option[0]">
            <div class="col-5">
              <label>{{ getLanguageName(option[0]) }}</label>
            </div>
            <div class="col-6">
              <Select
                :elements="getAvailableLanguageDialects(option[0])"
                v-model="newSettings.textToSpeechDialects[option[0]]"
                @change="checkSettings"
                keyAttr="label"
                valueAttr="id"
                class="white-select"
              ></Select>
            </div>
            <div class="col-1">
              <i class="icon icon-delete" @click="removeDialect(option[0])"></i>
            </div>
          </div>
          <div class="row table-row">
            <div class="col-4">
              <Select
                :elements="getAvailableDialectLanguages()"
                v-model="newDialectLanguage"
                @change="checkSettings"
                keyAttr="label"
                valueAttr="id"
                class="white-select small"
                defaultText="Select language"
              ></Select>
            </div>
            <div class="col-4">
              <Select
                :elements="getAvailableLanguageDialects(newDialectLanguage)"
                v-model="newDialectPreference"
                @change="checkSettings"
                keyAttr="label"
                valueAttr="id"
                class="white-select"
                defaultText="Select dialect"
              ></Select>
            </div>
            <div class="col-4">
              <button class="btn medium btn-full-width btn--purple gradient" type="button" @click="addNewDialect" :disabled="invalidSettings.length > 0">
                Add new dialect
              </button>
            </div>
          </div>
        </div>

        <div v-show="options.textToSpeechResponseFormat" class="form-group--inside">
          <label>Format</label>
          <Select
            :elements="options.textToSpeechResponseFormat"
            v-model="newSettings.textToSpeechResponseFormat"
            @change="otherOptionsCouldChange"
            keyAttr="label"
            valueAttr="id"
            class="white-select"
          ></Select>
        </div>

        <div class="form-group--inside">
          <label>Silence until idle speech.</label><br />
          <input type="range" :value="newSettings.idleTimeout" :min="0.5" :max="10" :step="0.1" @change="changeIdleTimeout" />
          {{ newSettings.idleTimeout }} seconds
        </div>

        <div class="form-group--inside">
          <label>Random idle phrases to fill silence. Separate with enter.</label><br />
          <textarea :value="newSettings.idleText" @change="changeIdleText"></textarea>
        </div>

        <div v-show="options.textToSpeechModel" class="form-group--inside">
          <label>Model - {{ newSettings.textToSpeechModel }}</label>
          <Select
            :elements="options.textToSpeechModel"
            v-model="newSettings.textToSpeechModel"
            @change="otherOptionsCouldChange"
            keyAttr="label"
            valueAttr="id"
            class="white-select"
          ></Select>
        </div>

        <div v-show="['gpt-4o-audio-preview', 'gpt-4o-mini-tts'].includes(newSettings.textToSpeechModel)" class="form-group--inside">
          <label>Instructions</label>
          <textarea :value="newSettings.textToSpeechInstructions" @change="changeInstructions"></textarea>
        </div>

        <div v-show="options.textToSpeechStreaming" class="form-group--inside">
          <Toggle v-model="newSettings.textToSpeechStreaming" @change="otherOptionsCouldChange" label="streaming"></Toggle>
        </div>
      </div>
      <div v-show="invalidSettings.length > 0" class="form-group--inside invalid">
        Please check:
        <span v-for="(_el, index) in invalidSettings">
          {{ invalidSettings[index] }}
        </span>
      </div>
      <button class="btn btn--purple gradient" type="button" @click="save()" :disabled="invalidSettings.length > 0">
        Save settings <i class="icon icon-save"></i>
      </button>
    </div>
  </Modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import FormGroup from '../../../../../helpers/FormGroup.vue'
import Autocomplete from '../../../../../helpers/Autocomplete.vue'
import Select from '../../../../../helpers/Select.vue'
import Modal from '../../../../../helpers/Modal.vue'
import Toggle from '../../../../../helpers/Toggle.vue'
import { ExternalProvider } from '@common/enums/external-provider.enum'
import { IBotSettings } from '@common/interfaces/bot-settings.interface'
import { SettingsHelper, TTSOptions, TTSSettings } from '@common/helpers/settings.helper'
import { getProviderVoiceIds, isoLanguageNames, languageRegionNames } from '@common/helpers/tts.helper'

@Component({
  computed: {
    ExternalProvider() {
      return ExternalProvider
    }
  },
  components: {
    FormGroup,
    Autocomplete,
    Select,
    Toggle,
    Modal
  }
})
export default class TTSConfiguration extends Vue {
  @Prop({ default: ExternalProvider.Microsoft }) provider: ExternalProvider
  @Prop() settings: TTSSettings
  newSettings: TTSSettings
  defaultSettings: TTSSettings
  options: TTSOptions
  invalidSettings = []
  newDialectLanguage = undefined
  newDialectPreference = undefined

  languiagesWithDialects: {[key: string]: Set<string>} = {}

  speedMin = 0.3
  speedMax = 4.0
  speedStep = 0.1

  /**
   * Gets all languages that have more than 1 variation - e.g. en-US, en-GB
   * For each of those languages, it returns the language code and the language name.
   * @returns {Array<{ id: string, label: string }>}
   */
  getAvailableDialectLanguages() {
    return Object.entries(this.languiagesWithDialects)
      .filter(entry => (entry[1] as Set<any>).size > 1)
      .filter(entry => !this.newSettings?.textToSpeechDialects?.[entry[0]])
      .map(entry => ({ id: entry[0], label: isoLanguageNames[entry[0]]  }))
  }

  /**
   * For a specific language that has dialects, it returns the dialects available for that language.
   * For example, for 'en', it returns 'en-US', 'en-GB', etc. together with a human-readable name.
   * 
   * @param language - the language code (e.g. 'en')
   * @returns {Array<{ id: string, label: string }>}
   */
  getAvailableLanguageDialects(language: string) {
    return [...(this.languiagesWithDialects[language] || new Set())]
      .map(dialect => ({ id: dialect, label: languageRegionNames[dialect] }))
  }

  /**
   * Returns the human-readable name of a language based on its key.
   * @param key the language key (e.g. 'en', 'fr', etc.)
   */
  getLanguageName(key: string) {
    return isoLanguageNames[key]
  }

  created() {
    this.defaultSettings = SettingsHelper.getTTSDefault(this.provider)
    this.newSettings = JSON.parse(JSON.stringify({ ...this.defaultSettings, ...this.settings })) // deep clone settings.
    this.options = SettingsHelper.getTTSOptions(this.provider, { ...this.defaultSettings, ...this.settings })
    this.checkSettings()

    // For the selected provider, get the voices that it supports.
    const supportedVoices = getProviderVoiceIds(this.provider)
    
    /**
     * Calculate the languages with dialects.
     * A language has a dialect if it has more than one variation, e.g. en-US, en-GB.
     * The dialects are stored in a Set to avoid duplicates.
     */
    this.languiagesWithDialects = Object.keys(supportedVoices).reduce((acc, voice) => {
      const lang = voice.split('-')[0]
      const dialect = voice.split('-')[1]
      if(!acc[lang]) {
        acc[lang] = new Set()
      }
      acc[lang].add(lang + '-' + dialect)
      return acc
    }, {})
  }

  save() {
    const diff: Partial<IBotSettings> = {}
    for (const key of Object.keys(this.defaultSettings)) {
      if (this.newSettings[key] !== this.settings[key]) {
        diff[key] = this.newSettings[key]
      }
    }
    // eslint-disable-next-line no-console
    this.$emit('save', diff) // --> Configuration.save --> Settings.saveSettings --> onFormSubmit --> ConfigurationManagementMixin.updateConfiguration
    this.$emit('close')
  }

  changeSpeed(e: InputEvent) {
    this.newSettings.textToSpeechSpeed = parseFloat((e.target as HTMLInputElement).value)
    console.log('changeSpeed', this.newSettings.textToSpeechSpeed)
  }

  changeIdleTimeout(e: InputEvent) {
    this.newSettings.idleTimeout = parseFloat((e.target as HTMLInputElement).value)
  }

  changeIdleText(e: InputEvent) {
    this.newSettings.idleText = (e.target as HTMLInputElement).value
  }

  changeInstructions(e: InputEvent) {
    this.newSettings.textToSpeechInstructions = (e.target as HTMLInputElement).value
  }

  addNewDialect() {
    if(!this.newDialectPreference) {
      return
    }

    this.newSettings.textToSpeechDialects = {
      ...this.newSettings.textToSpeechDialects,
      [this.newDialectLanguage]: this.newDialectPreference
    }

    this.newDialectLanguage = undefined
    this.newDialectPreference = undefined
    this.$forceUpdate()
    // this.options.textToSpeechDialects['newKey'] = 'test'
    // Vue.set(this.options.textToSpeechDialects, 'newKey', "test")
  }

  removeDialect(key: string) {
    delete this.newSettings.textToSpeechDialects[key]
    this.$forceUpdate()
  }

  /**
   * Certain option change other options,
   * e.g. streaming with TTS model --> output format is only mp3
   */
  otherOptionsCouldChange() {
    this.options = SettingsHelper.getTTSOptions(this.provider, this.newSettings)
    this.checkSettings()
  }
  checkSettings() {
    this.invalidSettings = []
    // Check if all settings have an allowed value.
    for (const key of Object.keys(this.options)) {
      const currentValue = this.newSettings[key]
      const allowedValues = this.options[key]
      if (Array.isArray(allowedValues)) {
        // Multiple choice
        if (!allowedValues.some((item) => Object.values(item).includes(currentValue))) {
          // The current value is no longer valid because
          // a. it was set for a different TTS providers.
          // b. some other option restricted the allowed values.
          this.newSettings[key] = null // clear current value and force select menu to change.
          this.invalidSettings.push(key)
          return
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.invalid {
  color: red;
}

.row {
  align-items: center;
}

.table-row {
  margin-bottom: 1em;

  .white-select {
    margin: 0;
  }

}

</style>
